import { FC, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'

import { Search } from '@mui/icons-material'
import {
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  Pagination,
  Skeleton,
  Stack,
  TableContainer,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import { skipToken } from '@reduxjs/toolkit/query'

import { GRTable, GRTableColumn } from '../../../components/GRTable/GRTable'
import { GRTooltip } from '../../../components/GRTooltip/GRTooltip'
import { Market } from '../../../features/markets/types/Market'
import { Store } from '../../../features/store/types/Store'
import usePage from '../../../hooks/usePage'
import pageService from '../../../services/PageService'
import { useGetInternalCategoriesQuery, useGetInternalCategoryFeaturesQuery } from '../../api/categories'
import { useGetInternalMarketsQuery } from '../../api/internal-markets'
import { useGetInternalStoresQuery } from '../../api/stores'
import { useAddInternalTagTargetsMutation, useGetInternalTagsQuery, useRemoveInternalTagTargetsMutation } from '../../api/tags'
import { BackButton } from '../../components/BackButton/BackButton'
import { CheckedStatusCircle } from '../../components/CheckedStatusCircle/CheckedStatusCircle'
import { DiscardChangesDialog } from '../../components/DiscardChangesDialog/DiscardChangesDialog'
import {
  assignTagTypes,
  buildFeatureRows,
  checkStoredChanges,
  FeatureRow,
  getAssignableTags,
  getEffectiveTargets,
  getPositionCount,
  getPositionNames,
  resolveAssignTag,
  TagTargetChanges,
  toggleTarget,
  toTargetRequests,
} from '../../features/tags/components/helpers/tagAssignHelpers'
import { tagTypeLabelKeys } from '../../features/tags/components/helpers/tagHelpers'
import { useEditPageLeave } from '../../hooks/useEditPageLeave'
import { useMutationFeedback } from '../../hooks/useMutationFeedback'
import { InternalTag, InternalTagType } from '../../types/InternalTag'

const pageSize = 50
// The category map is the same for every market.
const categoriesMarketIso = 'us'
type CustomProps = {
  tag?: InternalTag
  selectedTargets: Set<number>
  stores?: Store[]
  markets?: Market[]
  isSaving: boolean
  onToggle: (id: number) => void
}

export const TagsAssignPage: FC = () => {
  usePage(pageService.getInternalPageWithId('internal-tags-assign'))
  const { t } = useTranslation()
  const { notifySuccess, notifyError } = useMutationFeedback()
  const [searchParams, setSearchParams] = useSearchParams()

  const tagsQuery = useGetInternalTagsQuery()
  const categoriesQuery = useGetInternalCategoriesQuery({ marketIso: categoriesMarketIso })
  const storesQuery = useGetInternalStoresQuery()
  const marketsQuery = useGetInternalMarketsQuery()
  const [addTargets] = useAddInternalTagTargetsMutation()
  const [removeTargets] = useRemoveInternalTagTargetsMutation()

  const tags = useMemo(() => tagsQuery.data ?? [], [tagsQuery.data])
  const typeParam = searchParams.get('type')
  const tagIdParam = searchParams.get('tagId')
  const { type, tag } = useMemo(() => resolveAssignTag(tags, typeParam, tagIdParam), [tags, typeParam, tagIdParam])
  const typeTags = useMemo(() => getAssignableTags(tags, type), [tags, type])

  const categories = useMemo(() => categoriesQuery.data ?? [], [categoriesQuery.data])
  const [categoryId, setCategoryId] = useState<string>()
  const category = categories.find((item) => item.id === categoryId) ?? categories[0]
  const featuresQuery = useGetInternalCategoryFeaturesQuery(category ? { categoryId: category.id } : skipToken)

  const [activeOnly, setActiveOnly] = useState(true)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [changes, setChanges] = useState<TagTargetChanges>({})
  const [isSaving, setIsSaving] = useState(false)

  const rows = useMemo(() => buildFeatureRows(featuresQuery.currentData ?? [], activeOnly, search), [featuresQuery.currentData, activeOnly, search])
  const pageCount = Math.max(1, Math.ceil(rows.length / pageSize))
  const currentPage = Math.min(page, pageCount)
  const pageRows = useMemo(() => rows.slice((currentPage - 1) * pageSize, currentPage * pageSize), [rows, currentPage])

  const selectedTargets = useMemo(() => new Set(tag ? getEffectiveTargets(tag, changes[tag.id]) : []), [tag, changes])
  const tagName = useCallback((id: string) => tags.find((item) => item.id === id)?.name ?? id, [tags])
  const changedTagNames = Object.keys(changes).map(tagName)
  const hasChanges = changedTagNames.length > 0
  const { guard } = useEditPageLeave(hasChanges)

  const tableColumns: GRTableColumn<FeatureRow, CustomProps>[] = useMemo(
    () => [
      {
        labelAccessor: t('internal-service-management:tags_assign_number'),
        accessor: ({ row }) => row.number,
        headerCellProps: { sx: { width: 60 } },
      },
      {
        labelAccessor: t('internal-service-management:tags_assign_feature'),
        accessor: ({ row }) => <Typography fontWeight="bold">{row.feature.name || '---'}</Typography>,
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { minWidth: 220 } },
      },
      {
        labelAccessor: t('internal-service-management:tags_active'),
        accessor: ({ row }) => (
          <Box component="span" role="img" aria-label={t(row.feature.active ? 'internal-common:yes' : 'internal-common:no')} display="inline-flex">
            <CheckedStatusCircle value={row.feature.active} />
          </Box>
        ),
        headerCellProps: { sx: { width: 80 } },
      },
      {
        labelAccessor: t('internal-service-management:tags_assign_markets'),
        accessor: ({ row, customTableProps }) => {
          const names = getPositionNames(row.feature, customTableProps?.stores, customTableProps?.markets)
          return (
            <GRTooltip
              content={
                <Box>
                  <Typography variant="body2" fontWeight="bold" mb={0.5}>
                    {t('internal-service-management:tags_assign_markets')}
                  </Typography>
                  {names.length ? (
                    names.map((name, index) => (
                      <Typography key={`${name}-${index}`} variant="body2">
                        {name}
                      </Typography>
                    ))
                  ) : (
                    <Typography variant="body2">{t('internal-service-management:tags_assign_no_markets')}</Typography>
                  )}
                </Box>
              }
            >
              <Typography component="span" tabIndex={0} sx={{ textDecoration: 'underline dotted', cursor: 'default' }}>
                {getPositionCount(row.feature)}
              </Typography>
            </GRTooltip>
          )
        },
        headerCellProps: { sx: { width: 100 } },
      },
      {
        labelAccessor: t('internal-service-management:tags_assign_assigned'),
        accessor: ({ row, customTableProps }) => {
          if (!customTableProps?.tag) return null
          const { tag: currentTag, selectedTargets: selected, isSaving: saving, onToggle } = customTableProps
          if (currentTag.type === InternalTagType.Feature) {
            return (
              <ToggleButton
                size="small"
                color="primary"
                value={row.feature.legacyId}
                selected={selected.has(row.feature.legacyId)}
                disabled={saving}
                onChange={() => onToggle(row.feature.legacyId)}
              >
                {t('internal-service-management:tags_assign_selected')}
              </ToggleButton>
            )
          }
          return (
            <Stack direction="row" flexWrap="wrap" gap={0.5}>
              {row.feature.choices.map((choice) => (
                <ToggleButton
                  key={choice.weightId}
                  size="small"
                  color="primary"
                  value={choice.weightId}
                  selected={selected.has(choice.weightId)}
                  disabled={saving}
                  onChange={() => onToggle(choice.weightId)}
                  sx={{ textTransform: 'none' }}
                >
                  {choice.choice}
                </ToggleButton>
              ))}
            </Stack>
          )
        },
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { minWidth: 320 } },
      },
    ],
    [t]
  )
  const [columns, setColumns] = useState(tableColumns)

  const customProps = useMemo<CustomProps>(
    () => ({
      tag,
      selectedTargets,
      stores: storesQuery.data,
      markets: marketsQuery.data,
      isSaving,
      onToggle: (id) => tag && setChanges((current) => toggleTarget(current, tag, id)),
    }),
    [isSaving, marketsQuery.data, selectedTargets, storesQuery.data, tag]
  )

  const selectTag = (nextType: InternalTagType, nextTagId?: string) => {
    setSearchParams(nextTagId ? { type: nextType, tagId: nextTagId } : { type: nextType }, { replace: true })
  }

  const handleSave = useCallback(async () => {
    setIsSaving(true)
    let requestFailed = false
    for (const request of toTargetRequests(changes, tags)) {
      try {
        const send = request.action === 'add' ? addTargets : removeTargets
        await send({ type: request.type, targets: request.targets }).unwrap()
      } catch {
        requestFailed = true
        break
      }
    }
    try {
      const stored = await tagsQuery.refetch().unwrap()
      const { remaining, failedTagIds } = checkStoredChanges(changes, stored)
      setChanges(remaining)
      if (requestFailed) notifyError(t('internal-service-management:tags_assign_save_failed'))
      else if (failedTagIds.length) notifyError(t('internal-service-management:tags_assign_not_stored', { tags: failedTagIds.map(tagName).join(', ') }))
      else notifySuccess(t('internal-service-management:tags_assign_saved'))
    } catch {
      notifyError(t('internal-service-management:tags_assign_save_failed'))
    } finally {
      setIsSaving(false)
    }
  }, [addTargets, changes, notifyError, notifySuccess, removeTargets, t, tagName, tags, tagsQuery])

  const isLoading = tagsQuery.isLoading || categoriesQuery.isLoading
  const isError = tagsQuery.isError || categoriesQuery.isError
  const saveButton = (
    <Button variant="contained" onClick={handleSave} disabled={!hasChanges || isSaving}>
      {t('internal-service-management:tags_assign_save')}
    </Button>
  )

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2} mb={2}>
        <Stack direction="row" alignItems="center" gap={2}>
          <BackButton url={`/internal/tags?type=${type}`} mb={0} text={t('internal-service-management:tags_assign_back')} />
          <Typography variant="h5" fontWeight="bold">
            {t('internal-service-management:tags_assign_title')}
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="center" gap={2}>
          {hasChanges && (
            <GRTooltip
              content={
                <Box>
                  <Typography variant="body2" fontWeight="bold" mb={0.5}>
                    {t('internal-service-management:tags_assign_unsaved_tags')}
                  </Typography>
                  {changedTagNames.map((name) => (
                    <Typography key={name} variant="body2">
                      {name}
                    </Typography>
                  ))}
                </Box>
              }
            >
              <Chip
                color="warning"
                variant="outlined"
                tabIndex={0}
                label={t('internal-service-management:tags_assign_unsaved', { count: changedTagNames.length })}
              />
            </GRTooltip>
          )}
          {saveButton}
        </Stack>
      </Stack>

      {isLoading ? (
        <Skeleton variant="rectangular" height={320} />
      ) : isError ? (
        <Typography color="error.main">{t('internal-service-management:tags_assign_load_failed')}</Typography>
      ) : (
        <>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap mb={2}>
            <ToggleButtonGroup
              exclusive
              size="small"
              color="primary"
              value={type}
              onChange={(_, value: InternalTagType | null) => value && value !== type && selectTag(value)}
              aria-label={t('internal-service-management:tags_type')}
            >
              {assignTagTypes.map((tagType) => (
                <ToggleButton key={tagType} value={tagType}>
                  {t(tagTypeLabelKeys[tagType])}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <TextField
              select
              size="small"
              label={t('internal-service-management:tags_assign_tag_label')}
              value={tag?.id ?? ''}
              onChange={(event) => selectTag(type, event.target.value)}
              sx={{ minWidth: 260 }}
            >
              {typeTags.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </TextField>
            {tag && !tag.active && (
              <Typography variant="body2" color="error.main">
                {t('internal-service-management:tags_assign_tag_inactive')}
              </Typography>
            )}
            {!tag && <Typography variant="body2">{t('internal-service-management:tags_assign_no_tags')}</Typography>}
          </Stack>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap mb={2}>
            <TextField
              select
              size="small"
              label={t('internal-service-management:tags_assign_category')}
              value={category?.id ?? ''}
              onChange={(event) => {
                setCategoryId(event.target.value)
                setPage(1)
              }}
              sx={{ minWidth: 220 }}
            >
              {categories.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </TextField>
            <FormControlLabel
              control={
                <Checkbox
                  checked={activeOnly}
                  onChange={(event) => {
                    setActiveOnly(event.target.checked)
                    setPage(1)
                  }}
                />
              }
              label={t('internal-service-management:tags_assign_active_only')}
            />
            <TextField
              size="small"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value)
                setPage(1)
              }}
              placeholder={t('internal-service-management:tags_assign_search')}
              inputProps={{ 'aria-label': t('internal-service-management:tags_assign_search') }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 280 }}
            />
          </Stack>

          {featuresQuery.isError ? (
            <Typography color="error.main">{t('internal-service-management:tags_assign_features_load_failed')}</Typography>
          ) : (
            <TableContainer component={Card}>
              <GRTable
                rows={pageRows}
                columns={columns}
                onColumnsUpdated={setColumns}
                rowIdKey={(row) => row.feature.id}
                customProps={customProps}
                isLoading={featuresQuery.isFetching}
                noRowsLabel={t('internal-service-management:tags_assign_no_features')}
                striped
                hoverable
              />
            </TableContainer>
          )}
          <Stack direction="row" alignItems="center" justifyContent="space-between" mt={2}>
            {pageCount > 1 ? <Pagination count={pageCount} page={currentPage} onChange={(_, value) => setPage(value)} color="primary" /> : <Box />}
            {saveButton}
          </Stack>
        </>
      )}

      <DiscardChangesDialog
        guard={guard}
        title={t('internal-service-management:tags_assign_leave_title')}
        text={t('internal-service-management:tags_assign_leave_confirm', { tags: changedTagNames.join(', ') })}
        discardText={t('internal-service-management:tags_assign_leave')}
      />
    </Box>
  )
}
